import React from 'react';
import { DashboardMetrics } from '../../../shared/types/dashboard';

type Metric = {
  label: string;
  value: string;
  change: string;
  positive: boolean;
  benchmark?: string;
  benchmarkPositive?: boolean;
  icon: React.ReactNode;
  color?: string;
};

interface AccountSummaryProps {
  data?: DashboardMetrics | null;
  isLoading?: boolean;
}

const formatNumber = (num: number) => {
  if (num === undefined || num === null) return '0';
  const absNum = Math.abs(num);
  let formatted = '';
  if (absNum >= 1000000) formatted = (absNum / 1000000).toFixed(2) + ' M';
  else if (absNum >= 10000) formatted = (absNum / 1000).toFixed(2) + ' k';
  else if (absNum >= 1000) formatted = (absNum / 1000).toFixed(2) + ' k';
  else formatted = Number.isInteger(absNum) ? absNum.toString() : absNum.toFixed(2);
  
  return num < 0 ? `-${formatted}` : formatted;
};

const formatBenchmark = (num: number | null | undefined) => {
  if (num === null) return 'vs - avg';
  if (num === undefined) return undefined;
  return `vs ${formatNumber(num)} avg`;
};

const AccountSummary = ({ data, isLoading }: AccountSummaryProps) => {
  if (isLoading) {
    return (
      <div className="grid grid-cols-2 gap-3 mb-8 lg:grid-cols-4">
        {[1, 2, 3, 4].map((i) => (
          <div key={i} className="bg-slate-900/50 backdrop-blur-sm p-3 sm:p-6 rounded-2xl border border-slate-800 animate-pulse h-24"></div>
        ))}
      </div>
    );
  }

  const metrics: Metric[] = [
    {
      label: 'Total Followers',
      value: formatNumber(data?.followers || 0),
      change: `${data?.followersChange && data.followersChange >= 0 ? '+' : ''}${formatNumber(data?.followersChange || 0)} (${(data?.followersChangePercent || 0).toFixed(1)}%)`,
      positive: (data?.followersChange || 0) >= 0,
      benchmark: formatBenchmark(data?.followersBenchmarkAvg),
      benchmarkPositive: (data?.followersBenchmarkAvg || 0) >= 0, // Simplified benchmark logic
      color: '#38bdf8',
      icon: (
        <svg fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-6 h-6">
          <path strokeLinecap="round" strokeLinejoin="round" d="M15 19.128a9.38 9.38 0 002.625.372 9.337 9.337 0 004.121-.952 4.125 4.125 0 00-7.533-2.493M15 19.128v-.003c0-1.113-.285-2.16-.786-3.07M15 19.128v.106A12.318 12.318 0 018.624 21c-2.331 0-4.512-.645-6.374-1.766l-.001-.109a6.375 6.375 0 0111.964-3.07M12 6.375a3.375 3.375 0 11-6.75 0 3.375 3.375 0 016.75 0zm8.25 2.25a2.625 2.625 0 11-5.25 0 2.625 2.625 0 015.25 0z" />
        </svg>
      ),
    },
    {
      label: 'Total Likes',
      value: formatNumber(data?.likes || 0),
      change: `${data?.likesChange && data.likesChange >= 0 ? '+' : ''}${formatNumber(data?.likesChange || 0)} (${(data?.likesChangePercent || 0).toFixed(1)}%)`,
      positive: (data?.likesChange || 0) >= 0,
      benchmark: formatBenchmark(data?.likesBenchmarkAvg),
      benchmarkPositive: (data?.likesBenchmarkAvg || 0) >= 0,
      color: '#EC0452',
      icon: (
        <svg fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-6 h-6">
           <path strokeLinecap="round" strokeLinejoin="round" d="M21 8.25c0-2.485-2.099-4.5-4.688-4.5-1.935 0-3.597 1.126-4.312 2.733-.715-1.607-2.377-2.733-4.313-2.733C5.1 3.75 3 5.765 3 8.25c0 7.22 9 12 9 12s9-4.78 9-12z" />
        </svg>
      ),
    },
    {
      label: 'Total Views',
      value: formatNumber(data?.views || 0),
      change: `${data?.viewsChange && data.viewsChange >= 0 ? '+' : ''}${formatNumber(data?.viewsChange || 0)} (${(data?.viewsChangePercent || 0).toFixed(1)}%)`,
      positive: (data?.viewsChange || 0) >= 0,
      benchmark: formatBenchmark(data?.viewsBenchmarkAvg),
      benchmarkPositive: (data?.viewsBenchmarkAvg || 0) >= 0,
      color: '#22C55E',
      icon: (
        <svg fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-6 h-6">
          <path strokeLinecap="round" strokeLinejoin="round" d="M2.036 12.322a1.012 1.012 0 010-.639C3.423 7.51 7.36 4.5 12 4.5c4.638 0 8.573 3.007 9.963 7.178.07.207.07.431 0 .639C20.577 16.49 16.64 19.5 12 19.5c-4.638 0-8.573-3.007-9.963-7.178z" />
          <path strokeLinecap="round" strokeLinejoin="round" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
        </svg>
      ),
    },
    {
      label: 'Videos Posted',
      value: formatNumber(data?.videosPosted || 0),
      change: `${data?.videosPostedChange !== undefined && data.videosPostedChange >= 0 ? '+' : ''}${data?.videosPostedChange || 0}`,
      positive: (data?.videosPostedChange || 0) >= 0,
      benchmark: undefined,
      benchmarkPositive: undefined,
      color: '#14B8A6',
      icon: (
        <svg fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-6 h-6">
          <path strokeLinecap="round" strokeLinejoin="round" d="m15.75 10.5 4.72-4.72a.75.75 0 0 1 1.28.53v11.38a.75.75 0 0 1-1.28.53l-4.72-4.72M4.5 18.75h9a2.25 2.25 0 0 0 2.25-2.25v-9a2.25 2.25 0 0 0-2.25-2.25h-9A2.25 2.25 0 0 0 2.25 7.5v9a2.25 2.25 0 0 0 2.25 2.25Z" />
        </svg>
      ),
    },
  ];

  return (
    <div className="grid grid-cols-2 gap-3 mb-8 lg:grid-cols-4">
      {metrics.map((metric, idx) => (
        <div key={idx} className="bg-slate-900/50 backdrop-blur-sm p-3 sm:p-6 rounded-2xl shadow-lg border border-slate-800 flex items-center justify-between gap-2 sm:gap-4 relative overflow-hidden group">
          <div className="min-w-0">
            <p className="text-xs sm:text-sm font-medium text-slate-400 truncate mb-0.5 sm:mb-1">{metric.label}</p>
            <h3 className="text-lg sm:text-2xl font-bold text-white truncate">{metric.value}</h3>
            
            {metric.change && (
              <div className="space-y-1 mt-1 sm:mt-2">
                <div className={`flex items-center text-[10px] sm:text-xs font-medium ${metric.positive ? 'text-emerald-400' : 'text-rose-400'}`}>
                  <span className="mr-1">{metric.positive ? '▲' : '▼'}</span>
                  {metric.change}
                </div>
                
                {metric.benchmark && (
                  <div className="text-[9px] sm:text-[10px] opacity-100 flex items-center text-slate-300 font-medium">
                    <span className="mr-1">{metric.benchmarkPositive ? '★' : '○'}</span>
                    {metric.benchmark}
                  </div>
                )}
              </div>
            )}
          </div>
          <div
            className="p-2 sm:p-3 bg-slate-800 rounded-xl flex-shrink-0 group-hover:scale-110 transition-transform"
            style={{ color: metric.color || '#cbd5f5' }}
          >
            {metric.icon}
          </div>
        </div>
      ))}
    </div>
  );
};

export default AccountSummary;
